// src/pages/eglises/DetailsEglise.jsx
import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Spinner, Alert, Button, Badge, Table, Nav, Tab, ProgressBar } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import {
    LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    AreaChart, Area
} from 'recharts';
import Header from '../../layouts/Header';
import Footer from '../../layouts/Footer';
import { COLORS } from '../../constants/colors';

// ===== STYLES =====
const styles = `
  .dashboard-styled {
    background: #f4f6f9;
    min-height: 100vh;
  }

  /* En-tête moderne */
  .header-modern {
    background: white;
    padding: 1.2rem 1.8rem;
    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
    border-left: 4px solid ${COLORS.secondary};
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
  }

  .header-left {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .header-icon-wrapper {
    width: 48px;
    height: 48px;
    background: ${COLORS.primary};
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    color: white;
    font-size: 1.4rem;
  }

  .gradient-text {
    color: #1e293b;
    font-size: 1.3rem;
    font-weight: 700;
    margin: 0;
  }

  .badge-modern {
    background: #f1f4f9;
    color: #475569;
    padding: 0.3rem 0.8rem;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .badge-modern i {
    color: ${COLORS.secondary};
  }

  .btn-modern-outline {
    border: 1px solid #d1d5db;
    background: white;
    padding: 0.4rem 1.2rem;
    border-radius: 4px;
    font-size: 0.85rem;
    font-weight: 500;
    color: #4b5563;
    transition: all 0.2s;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .btn-modern-outline:hover {
    border-color: ${COLORS.secondary};
    color: ${COLORS.secondary};
    background: #fafafa;
  }

  .btn-modern-primary {
    background: ${COLORS.primary};
    border: none;
    padding: 0.4rem 1.2rem;
    border-radius: 4px;
    font-size: 0.85rem;
    font-weight: 500;
    color: white;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition: all 0.2s;
  }
  .btn-modern-primary:hover {
    background: ${COLORS.primaryDark};
    box-shadow: 0 4px 10px rgba(41,90,167,0.3);
  }

  /* Cartes */
  .card-modern {
    border: none;
    border-radius: 4px;
    background: white;
    box-shadow: 0 2px 6px rgba(0,0,0,0.04);
    border-top: 3px solid transparent;
    transition: box-shadow 0.2s;
  }
  .card-modern:hover {
    box-shadow: 0 4px 12px rgba(0,0,0,0.06);
  }
  .card-modern.border-primary {
    border-top-color: ${COLORS.primary};
  }
  .card-modern.border-secondary {
    border-top-color: ${COLORS.secondary};
  }

  .card-header-modern {
    background: transparent;
    border-bottom: 1px solid #e9edf2;
    padding: 0.8rem 1.2rem;
    font-weight: 600;
    color: #1e293b;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  .card-header-modern i {
    color: ${COLORS.secondary};
  }

  .card-body-modern {
    padding: 1.2rem;
  }

  /* KPI Cards */
  .kpi-card {
    background: white;
    border-radius: 4px;
    padding: 1rem;
    display: flex;
    align-items: center;
    gap: 1rem;
    box-shadow: 0 2px 6px rgba(0,0,0,0.04);
    transition: all 0.2s ease;
    height: 100%;
    border-left: 3px solid;
  }
  .kpi-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(0,0,0,0.06);
  }
  .kpi-primary { border-left-color: ${COLORS.primary}; }
  .kpi-success { border-left-color: ${COLORS.success}; }
  .kpi-warning { border-left-color: ${COLORS.warning}; }
  .kpi-danger { border-left-color: ${COLORS.danger}; }
  .kpi-info { border-left-color: ${COLORS.info}; }
  .kpi-purple { border-left-color: #8b5cf6; }
  .kpi-pink { border-left-color: #ec4899; }

  .kpi-icon {
    width: 44px;
    height: 44px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.2rem;
  }
  .kpi-primary .kpi-icon { background: ${COLORS.primary}15; color: ${COLORS.primary}; }
  .kpi-success .kpi-icon { background: ${COLORS.success}15; color: ${COLORS.success}; }
  .kpi-warning .kpi-icon { background: ${COLORS.warning}15; color: ${COLORS.warning}; }
  .kpi-danger .kpi-icon { background: ${COLORS.danger}15; color: ${COLORS.danger}; }
  .kpi-info .kpi-icon { background: ${COLORS.info}15; color: ${COLORS.info}; }
  .kpi-purple .kpi-icon { background: #8b5cf615; color: #8b5cf6; }
  .kpi-pink .kpi-icon { background: #ec489915; color: #ec4899; }

  .kpi-content { flex: 1; }
  .kpi-label {
    font-size: 0.7rem;
    text-transform: uppercase;
    color: #6c757d;
    font-weight: 600;
    letter-spacing: 0.5px;
  }
  .kpi-value {
    font-size: 1.3rem;
    font-weight: 700;
    margin: 0.1rem 0 0 0;
    color: #1e293b;
  }

  /* Onglets modernisés */
  .tab-modern {
    background: #f8fafc;
    padding: 0.5rem 1rem 0;
    border-radius: 4px 4px 0 0;
    border-bottom: 2px solid #e9edf2;
  }
  .tab-modern .nav-tabs {
    border-bottom: none;
  }
  .tab-modern .nav-link {
    border: none;
    color: #64748b;
    padding: 0.6rem 1.2rem;
    font-size: 0.85rem;
    font-weight: 500;
    transition: all 0.2s;
    border-radius: 4px 4px 0 0;
    position: relative;
    background: transparent;
  }



    /* ===== STYLES ENRICHIS POUR LES CARTES DES ONGLETS ===== */

  /* Conteneur des cartes - fond légèrement grisé pour contraster */
  .tab-content-modern {
    background: #f1f4f9;
    padding: 1.5rem;
    border-radius: 0 0 4px 4px;
    box-shadow: inset 0 2px 4px rgba(0,0,0,0.02);
  }

  /* ===== CARTES DE STATISTIQUES MINI (Membres) ===== */
  .stats-mini-card {
    background: white;
    border-radius: 4px;
    padding: 1rem 1.2rem;
    box-shadow: 0 4px 12px rgba(0,0,0,0.04);
    border: none;
    border-left: 4px solid ${COLORS.primary};
    transition: all 0.25s ease;
    height: 100%;
    display: flex;
    align-items: center;
    gap: 1rem;
    position: relative;
    overflow: hidden;
  }
  .stats-mini-card::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 80px;
    height: 80px;
    background: radial-gradient(circle, ${COLORS.primary}08, transparent 70%);
    border-radius: 50%;
    transform: translate(30%, -30%);
    pointer-events: none;
  }
  .stats-mini-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px rgba(41,90,167,0.08);
  }
  .stats-mini-card .stats-mini-icon {
    width: 48px;
    height: 48px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.3rem;
    flex-shrink: 0;
  }
  .stats-mini-card .stats-mini-content {
    flex: 1;
  }
  .stats-mini-card .stats-mini-label {
    font-size: 0.65rem;
    text-transform: uppercase;
    color: #94a3b8;
    letter-spacing: 0.5px;
    font-weight: 600;
  }
  .stats-mini-card .stats-mini-value {
    font-size: 1.5rem;
    font-weight: 700;
    line-height: 1.2;
    color: #1e293b;
  }
  /* Variantes de couleurs pour les mini-cartes */
  .stats-mini-card.primary { border-left-color: ${COLORS.primary}; }
  .stats-mini-card.primary .stats-mini-icon { background: ${COLORS.primary}15; color: ${COLORS.primary}; }
  .stats-mini-card.success { border-left-color: ${COLORS.success}; }
  .stats-mini-card.success .stats-mini-icon { background: ${COLORS.success}15; color: ${COLORS.success}; }
  .stats-mini-card.warning { border-left-color: ${COLORS.warning}; }
  .stats-mini-card.warning .stats-mini-icon { background: ${COLORS.warning}15; color: ${COLORS.warning}; }
  .stats-mini-card.danger { border-left-color: ${COLORS.danger}; }
  .stats-mini-card.danger .stats-mini-icon { background: ${COLORS.danger}15; color: ${COLORS.danger}; }
  .stats-mini-card.info { border-left-color: ${COLORS.info}; }
  .stats-mini-card.info .stats-mini-icon { background: ${COLORS.info}15; color: ${COLORS.info}; }
  .stats-mini-card.purple { border-left-color: #8b5cf6; }
  .stats-mini-card.purple .stats-mini-icon { background: #8b5cf615; color: #8b5cf6; }
  .stats-mini-card.pink { border-left-color: #ec4899; }
  .stats-mini-card.pink .stats-mini-icon { background: #ec489915; color: #ec4899; }

  /* ===== CARTES FINANCIÈRES ===== */
  .finance-stats-card {
    background: white;
    border-radius: 4px;
    padding: 1rem 1.2rem;
    box-shadow: 0 4px 12px rgba(0,0,0,0.04);
    border: none;
    border-top: 3px solid ${COLORS.primary};
    transition: all 0.25s ease;
    height: 100%;
    position: relative;
  }
  .finance-stats-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px rgba(41,90,167,0.08);
  }
  .finance-stats-card .finance-stats-label {
    font-size: 0.65rem;
    text-transform: uppercase;
    color: #94a3b8;
    letter-spacing: 0.5px;
    font-weight: 600;
  }
  .finance-stats-card .finance-stats-value {
    font-size: 1.4rem;
    font-weight: 700;
    margin: 0.2rem 0 0.1rem;
  }
  .finance-stats-card .finance-stats-trend {
    font-size: 0.7rem;
    color: #64748b;
  }
  /* Variantes de couleur */
  .finance-stats-card.entrees { border-top-color: ${COLORS.success}; }
  .finance-stats-card.depenses { border-top-color: ${COLORS.danger}; }
  .finance-stats-card.solde { border-top-color: ${COLORS.warning}; }
  .finance-stats-card.biens { border-top-color: ${COLORS.info}; }

  /* ===== CARTES ÉVÉNEMENTS ===== */
  .event-stats-card {
    background: white;
    border-radius: 4px;
    padding: 1rem 1.2rem;
    box-shadow: 0 4px 12px rgba(0,0,0,0.04);
    border: none;
    border-left: 4px solid ${COLORS.secondary};
    transition: all 0.25s ease;
    height: 100%;
    display: flex;
    align-items: center;
    gap: 1rem;
  }
  .event-stats-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px rgba(253,181,47,0.12);
  }
  .event-stats-card .event-stats-icon {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.3rem;
    flex-shrink: 0;
  }
  .event-stats-card .event-stats-label {
    font-size: 0.65rem;
    text-transform: uppercase;
    color: #94a3b8;
    letter-spacing: 0.5px;
    font-weight: 600;
  }
  .event-stats-card .event-stats-value {
    font-size: 1.4rem;
    font-weight: 700;
    color: #1e293b;
  }

  /* ===== CARTES STATISTIQUES DÉTAILLÉES ===== */
  .stats-detail-card {
    background: white;
    border-radius: 4px;
    padding: 0;
    box-shadow: 0 4px 12px rgba(0,0,0,0.04);
    border: 1px solid #e9edf2;
    transition: all 0.25s ease;
    height: 100%;
    overflow: hidden;
  }
  .stats-detail-card:hover {
    box-shadow: 0 8px 20px rgba(0,0,0,0.06);
  }
  .stats-detail-card .card-header-detail {
    background: ${COLORS.primary}08;
    padding: 0.6rem 1rem;
    border-bottom: 1px solid #e9edf2;
    font-weight: 600;
    color: ${COLORS.primary};
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  .stats-detail-card .card-header-detail i {
    font-size: 1rem;
  }
  .stats-detail-card .card-body-detail {
    padding: 0.8rem 1rem;
  }
  .stats-detail-item {
    display: flex;
    justify-content: space-between;
    padding: 0.4rem 0;
    font-size: 0.85rem;
    border-bottom: 1px solid #f1f4f9;
  }
  .stats-detail-item:last-child {
    border-bottom: none;
  }
  .stats-detail-item .label {
    color: #64748b;
  }
  .stats-detail-item .value {
    font-weight: 600;
    color: #1e293b;
  }

  /* ===== CARTES GRAPHIQUES ===== */
  .chart-card {
    background: white;
    border-radius: 4px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.04);
    border: 1px solid #e9edf2;
    transition: all 0.25s ease;
    overflow: hidden;
    height: 100%;
  }
  .chart-card:hover {
    box-shadow: 0 8px 20px rgba(0,0,0,0.06);
  }
  .chart-card .chart-card-header {
    background: ${COLORS.primary}05;
    padding: 0.6rem 1rem;
    border-bottom: 1px solid #e9edf2;
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-weight: 600;
    font-size: 0.85rem;
    color: #1e293b;
  }
  .chart-card .chart-card-header .badge-chart {
    font-weight: 500;
    padding: 0.2rem 0.6rem;
    border-radius: 4px;
    font-size: 0.65rem;
  }
  .chart-card .chart-body {
    padding: 0.5rem 0.5rem 0.2rem;
  }

  /* ===== TABLEAUX ===== */
  .table-card-modern {
    background: white;
    border-radius: 4px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.04);
    border: 1px solid #e9edf2;
    overflow: hidden;
  }
  .table-card-modern .table-header {
    background: ${COLORS.primary}05;
    padding: 0.6rem 1rem;
    border-bottom: 1px solid #e9edf2;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .table-card-modern table {
    font-size: 0.85rem;
  }
  .table-card-modern table thead th {
    background: #f8fafc;
    color: #475569;
    font-weight: 600;
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    border-bottom: 1px solid #e2e8f0;
  }
  .table-card-modern table tbody tr:hover {
    background: ${COLORS.primary}04;
  }

  /* ===== ANIMATION D'APPARITION ===== */
  .tab-pane .fade-in {
    animation: fadeSlide 0.3s ease-out;
  }
  @keyframes fadeSlide {
    0% { opacity: 0; transform: translateY(8px); }
    100% { opacity: 1; transform: translateY(0); }
  }


  .tab-modern .nav-link:hover {
    color: ${COLORS.primary};
    background: rgba(41,90,167,0.05);
  }
  .tab-modern .nav-link.active {
    color: ${COLORS.primary};
    background:  ${COLORS.secondary};
    font-weight: 600;
    box-shadow: 0 -2px 4px rgba(0,0,0,0.02);
  }
  .tab-modern .nav-link.active::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: ${COLORS.primary};
  }

  .tab-content-modern {
    background: white;
    padding: 1.5rem;
    border-radius: 0 0 4px 4px;
    box-shadow: 0 2px 6px rgba(0,0,0,0.04);
  }

  /* Info items */
  .info-item {
    padding: 0.6rem 0.8rem;
    background: #fafbfc;
    border-left: 2px solid ${COLORS.secondary};
    border-radius: 2px;
    margin-bottom: 0.5rem;
  }
  .info-item .info-label {
    font-size: 0.65rem;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    color: #94a3b8;
    font-weight: 600;
  }
  .info-item .info-value {
    font-size: 0.9rem;
    color: #1e293b;
    font-weight: 500;
  }
  .info-value.matricule {
    font-family: monospace;
    background: #f1f4f9;
    padding: 0.15rem 0.5rem;
    border-radius: 4px;
  }

  /* Location items */
  .location-item {
    display: flex;
    align-items: center;
    gap: 0.8rem;
    padding: 0.5rem 0;
    border-bottom: 1px solid #e9edf2;
  }
  .location-item:last-child {
    border-bottom: none;
  }
  .location-item i {
    font-size: 1.2rem;
    width: 24px;
    text-align: center;
  }
  .location-label {
    font-size: 0.65rem;
    text-transform: uppercase;
    color: #94a3b8;
  }
  .location-value {
    font-size: 0.85rem;
    font-weight: 500;
    color: #1e293b;
  }

  /* Logo */
  .logo-image {
    max-width: 100%;
    max-height: 150px;
    border-radius: 4px;
  }
  .logo-placeholder {
    height: 150px;
    background: #f1f4f9;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px dashed #d1d5db;
  }
  .logo-placeholder i {
    font-size: 3rem;
    color: #94a3b8;
  }

  /* Mini stats cards */
  .stats-mini-card {
    background: white;
    border-radius: 4px;
    padding: 0.8rem 1rem;
    display: flex;
    align-items: center;
    gap: 0.8rem;
    box-shadow: 0 2px 6px rgba(0,0,0,0.04);
    border: 1px solid #e9edf2;
    height: 100%;
  }
  .stats-mini-icon {
    width: 40px;
    height: 40px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.1rem;
  }
  .stats-mini-label {
    font-size: 0.65rem;
    text-transform: uppercase;
    color: #94a3b8;
  }
  .stats-mini-value {
    font-size: 1.2rem;
    font-weight: 700;
    color: #1e293b;
  }

  /* Finance stats */
  .finance-stats-card {
    background: white;
    border-radius: 4px;
    padding: 0.8rem 1rem;
    box-shadow: 0 2px 6px rgba(0,0,0,0.04);
    border: 1px solid #e9edf2;
    height: 100%;
  }
  .finance-stats-label {
    font-size: 0.65rem;
    text-transform: uppercase;
    color: #94a3b8;
  }
  .finance-stats-value {
    font-size: 1.2rem;
    font-weight: 700;
  }
  .finance-stats-trend {
    font-size: 0.7rem;
    color: #64748b;
  }

  /* Stats detail */
  .stats-detail-card {
    border: none;
    border-radius: 4px;
    background: white;
    box-shadow: 0 2px 6px rgba(0,0,0,0.04);
    border: 1px solid #e9edf2;
    height: 100%;
  }
  .stats-detail-title {
    font-size: 0.85rem;
    font-weight: 600;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid #e9edf2;
    color: ${COLORS.primary};
  }
  .stats-detail-item {
    display: flex;
    justify-content: space-between;
    padding: 0.3rem 0;
    font-size: 0.85rem;
    border-bottom: 1px solid #f8fafc;
  }
  .stats-detail-item:last-child {
    border-bottom: none;
  }

  /* Table */
  .table-modern {
    font-size: 0.85rem;
  }
  .table-modern thead th {
    background: #f8fafc;
    color: #475569;
    font-weight: 600;
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    border-bottom: 1px solid #e2e8f0;
  }
  .matricule-badge {
    background: #f1f4f9;
    padding: 0.15rem 0.5rem;
    border-radius: 4px;
    font-size: 0.7rem;
    font-family: monospace;
  }

  .action-btn {
    width: 28px;
    height: 28px;
    border-radius: 4px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none;
    transition: all 0.2s ease;
    font-size: 0.9rem;
  }
  .action-btn:hover {
    transform: scale(1.1);
    box-shadow: 0 4px 8px rgba(0,0,0,0.08);
  }
  .action-btn-view { background: ${COLORS.primary}15; color: ${COLORS.primary}; }
  .action-btn-view:hover { background: ${COLORS.primary}; color: white; }

  /* Chart cards */
  .chart-card {
    border: none;
    border-radius: 4px;
    background: white;
    box-shadow: 0 2px 6px rgba(0,0,0,0.04);
    border: 1px solid #e9edf2;
  }
  .chart-card-header {
    background: transparent;
    border-bottom: 1px solid #e9edf2;
    padding: 0.6rem 1rem;
    font-weight: 600;
    font-size: 0.85rem;
  }

  /* Loading */
  .loading-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 60vh;
  }
  .loading-ring-wrapper {
    position: relative;
    width: 70px;
    height: 70px;
  }
  .loading-ring {
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    border: 3px solid #e9edf2;
    border-top-color: ${COLORS.primary};
    animation: spin 0.8s linear infinite;
  }
  .loading-ring-inner {
    position: absolute;
    top: 8px;
    left: 8px;
    right: 8px;
    bottom: 8px;
    border-radius: 50%;
    border: 2px solid #e9edf2;
    border-bottom-color: ${COLORS.secondary};
    animation: spin 0.6s linear infinite reverse;
  }
  @keyframes spin { to { transform: rotate(360deg); } }

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(6px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .fade-in {
    animation: fadeIn 0.3s ease-out;
  }

  @media (max-width: 768px) {
    .header-modern {
      flex-direction: column;
      align-items: stretch;
      gap: 0.8rem;
      padding: 1rem;
    }
    .header-left {
      flex-wrap: wrap;
    }
    .tab-modern .nav-link {
      padding: 0.4rem 0.8rem;
      font-size: 0.75rem;
    }
  }
`;

// ===== PALETTE DES GRAPHIQUES =====
const CHART_COLORS = [
    COLORS.primary,
    COLORS.secondary,
    COLORS.success,
    COLORS.danger,
    '#8b5cf6',
    COLORS.info,
    COLORS.warning,
    '#ec4899'
];

export default function DetailsEglise() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [egliseData, setEgliseData] = useState(null);
    const [activeTab, setActiveTab] = useState('infos');
    const [imgError, setImgError] = useState(false);

    const [membresData, setMembresData] = useState({
        total: 0,
        hommes: 0,
        femmes: 0,
        enfants: 0,
        pasteurs: 0,
        anciens: 0,
        diacres: 0,
        nouveaux: 0,
        evolution: [],
        derniers_inscrits: []
    });

    const [financesData, setFinancesData] = useState({
        total_entrees: 0,
        total_depenses: 0,
        solde: 0,
        nombre_entrees: 0,
        nombre_depenses: 0,
        evolution: [],
        repartition_entrees: []
    });

    const [evenementsData, setEvenementsData] = useState({
        total_evenements: 0,
        total_participants: 0,
        mariages: 0,
        baptemes: 0,
        prochains: []
    });

    const [demographieData, setDemographieData] = useState({
        naissances: 0,
        deces: 0,
        croissance_naturelle: 0
    });

    const [patrimoineData, setPatrimoineData] = useState({
        total_biens: 0,
        total_quantite: 0
    });

    const [filiales, setFiliales] = useState([]);
    const [membres, setMembres] = useState([]);

    useEffect(() => {
        fetchEgliseDetails();
    }, [id]);

    const fetchEgliseDetails = async () => {
        try {
            setLoading(true);

            const userStr = localStorage.getItem('user');
            if (!userStr) {
                navigate('/pages/signin2');
                return;
            }

            const user = JSON.parse(userStr);
            const userId = user.id || user.id_utilisateur;

            const response = await fetch(`https://josephsbusiness.com/api/eglises_details_complete.php?user_id=${userId}&id=${id}`, {
                headers: { 'Accept': 'application/json' }
            });

            const result = await response.json();

            if (result.success) {
                setEgliseData(result);

                if (result.stats) {
                    setMembresData({
                        total: result.stats.membres?.total || 0,
                        hommes: result.stats.membres?.hommes || 0,
                        femmes: result.stats.membres?.femmes || 0,
                        enfants: result.stats.membres?.enfants || 0,
                        pasteurs: result.stats.membres?.pasteurs || 0,
                        anciens: result.stats.membres?.anciens || 0,
                        diacres: result.stats.membres?.diacres || 0,
                        nouveaux: result.stats.membres?.nouveaux || 0,
                        evolution: result.stats.membres?.evolution || [],
                        derniers_inscrits: result.stats.membres?.derniers_inscrits || []
                    });

                    setFinancesData({
                        total_entrees: result.stats.finances?.total_entrees || 0,
                        total_depenses: result.stats.finances?.total_depenses || 0,
                        solde: result.stats.finances?.solde || 0,
                        nombre_entrees: result.stats.finances?.nombre_entrees || 0,
                        nombre_depenses: result.stats.finances?.nombre_depenses || 0,
                        evolution: result.stats.finances?.evolution || [],
                        repartition_entrees: result.stats.finances?.repartition_entrees || []
                    });

                    setEvenementsData({
                        total_evenements: result.stats.evenements?.total_evenements || 0,
                        total_participants: result.stats.evenements?.total_participants || 0,
                        mariages: result.stats.evenements?.mariages || 0,
                        baptemes: result.stats.evenements?.baptemes || 0,
                        prochains: result.stats.evenements?.prochains || []
                    });

                    setDemographieData({
                        naissances: result.stats.demographie?.naissances || 0,
                        deces: result.stats.demographie?.deces || 0,
                        croissance_naturelle: result.stats.demographie?.croissance_naturelle || 0
                    });

                    setPatrimoineData({
                        total_biens: result.stats.patrimoine?.total_biens || 0,
                        total_quantite: result.stats.patrimoine?.total_quantite || 0
                    });
                }

                setFiliales(result.filiales || []);
                setMembres(result.membres || []);

            } else {
                setError(result.message || 'Erreur de chargement');
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const formatMontant = (montant) => {
        return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XAF' }).format(montant);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Non renseigné';
        return new Date(dateString).toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const getRoleBadgeColor = (role) => {
        const colors = {
            'Pasteur': 'danger',
            'Ancien': 'info',
            'Diacre': 'success',
            'Diaconesse': 'success',
            'Jeune': 'warning',
            'Nouveau': 'primary'
        };
        return colors[role] || 'secondary';
    };

    const generateFinancialData = () => {
        return financesData.evolution.length > 0 ? financesData.evolution : [
            { mois: 'Jan', entrees: 0, depenses: 0 },
            { mois: 'Fév', entrees: 0, depenses: 0 },
            { mois: 'Mar', entrees: 0, depenses: 0 },
            { mois: 'Avr', entrees: 0, depenses: 0 },
            { mois: 'Mai', entrees: 0, depenses: 0 },
            { mois: 'Juin', entrees: 0, depenses: 0 }
        ];
    };

    const generateRepartitionData = () => {
        if (!financesData.repartition_entrees || financesData.repartition_entrees.length === 0) {
            return [];
        }
        return financesData.repartition_entrees.map(item => {
            let name = item.type;
            if (name.includes('Dîme')) name = 'Dîmes';
            else if (name.includes('Deniers')) name = 'Offrandes';
            else if (name.includes('Caisse')) name = 'Caisse';
            else if (name.includes('Célébrations')) name = 'Célébrations';
            return { name, value: parseFloat(item.total) || 0 };
        }).filter(item => item.value > 0);
    };

    const generateRoleData = () => {
        return [
            { name: 'Pasteurs', value: membresData.pasteurs },
            { name: 'Anciens', value: membresData.anciens },
            { name: 'Diacres', value: membresData.diacres },
            { name: 'Nouveaux', value: membresData.nouveaux }
        ].filter(item => item.value > 0);
    };

     if (loading) {
       return (
           <>
               <Header />
               <div className="main main-app p-3 p-lg-4 dashboard-styled">
                   <div className="loading-wrapper">
                       <div className="text-center">
                           {/* Logo ou icône animé */}
                           {/* <div className="loading-icon-wrapper mb-4">
                               <div className="loading-icon-pulse">
                                   <i className="ri-building-line" style={{ fontSize: '3.5rem', color: COLORS.primary }}></i>
                               </div>
                           </div> */}
   
                           {/* Anneau de chargement amélioré */}
                           <div className="loading-ring-wrapper mb-4">
                               <div className="loading-ring"></div>
                               <div className="loading-ring-inner"></div>
                               <div className="loading-ring-dot"></div>
                           </div>
   
                           {/* Titre avec effet de gradient */}
                           <h5 className="fw-bold loading-title" style={{ 
                               color: COLORS.primary,
                               background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.secondary})`,
                               WebkitBackgroundClip: 'text',
                               WebkitTextFillColor: 'transparent',
                               backgroundClip: 'text',
                               fontSize: '1.4rem'
                           }}>
                               Chargement en cours
                           </h5>
   
                           {/* Message avec animation de points */}
                           <p className="text-muted small loading-message">
                               Récupération des données
                               <span className="loading-dots">
                                   <span>.</span><span>.</span><span>.</span>
                               </span>
                           </p>
   
                           {/* Barre de progression stylisée */}
                           <div className="loading-progress-wrapper mt-4">
                               <div className="loading-progress-track">
                                   <div className="loading-progress-bar"></div>
                               </div>
                           </div>
                       </div>
                   </div>
               </div>
               <Footer />
   
               {/* Styles intégrés pour le loading */}
               <style>{`
                   .loading-wrapper {
                       display: flex;
                       justify-content: center;
                       align-items: center;
                       min-height: 60vh;
                   }
   
                   /* Icône animée */
                   .loading-icon-wrapper {
                       position: relative;
                       width: 80px;
                       height: 80px;
                       margin: 0 auto;
                   }
                   .loading-icon-pulse {
                       animation: iconPulse 1.8s ease-in-out infinite;
                   }
                   @keyframes iconPulse {
                       0%, 100% { transform: scale(1); opacity: 1; }
                       50% { transform: scale(1.1); opacity: 0.6; }
                   }
   
                   /* Anneaux de chargement */
                   .loading-ring-wrapper {
                       position: relative;
                       width: 70px;
                       height: 70px;
                       margin: 0 auto;
                   }
                   .loading-ring {
                       position: absolute;
                       width: 100%;
                       height: 100%;
                       border-radius: 50%;
                       border: 3px solid #e9edf2;
                       border-top-color: ${COLORS.primary};
                       animation: spin 1s cubic-bezier(0.6, 0.2, 0.4, 0.8) infinite;
                   }
                   .loading-ring-inner {
                       position: absolute;
                       top: 8px;
                       left: 8px;
                       right: 8px;
                       bottom: 8px;
                       border-radius: 50%;
                       border: 2px solid #e9edf2;
                       border-bottom-color: ${COLORS.secondary};
                       animation: spin 0.7s cubic-bezier(0.4, 0.0, 0.2, 1) infinite reverse;
                   }
                   .loading-ring-dot {
                       position: absolute;
                       top: 50%;
                       left: 50%;
                       transform: translate(-50%, -50%);
                       width: 12px;
                       height: 12px;
                       border-radius: 50%;
                       background: ${COLORS.primary};
                       box-shadow: 0 0 20px ${COLORS.primary}40;
                       animation: dotPulse 1.2s ease-in-out infinite;
                   }
                   @keyframes spin {
                       to { transform: rotate(360deg); }
                   }
                   @keyframes dotPulse {
                       0%, 100% { transform: translate(-50%, -50%) scale(0.8); opacity: 0.6; }
                       50% { transform: translate(-50%, -50%) scale(1.2); opacity: 1; }
                   }
   
                   /* Titre avec dégradé (déjà géré en inline) */
                   .loading-title {
                       margin-bottom: 0.25rem;
                   }
   
                   /* Message avec points animés */
                   .loading-message {
                       font-size: 0.9rem;
                       color: #94a3b8;
                   }
                   .loading-dots span {
                       animation: dots 1.4s infinite;
                       opacity: 0;
                   }
                   .loading-dots span:nth-child(1) { animation-delay: 0s; }
                   .loading-dots span:nth-child(2) { animation-delay: 0.2s; }
                   .loading-dots span:nth-child(3) { animation-delay: 0.4s; }
                   @keyframes dots {
                       0% { opacity: 0; }
                       50% { opacity: 1; }
                       100% { opacity: 0; }
                   }
   
                   /* Barre de progression */
                   .loading-progress-wrapper {
                       max-width: 250px;
                       margin: 0 auto;
                   }
                   .loading-progress-track {
                       width: 100%;
                       height: 4px;
                       background: #e9edf2;
                       border-radius: 4px;
                       overflow: hidden;
                   }
                   .loading-progress-bar {
                       width: 0%;
                       height: 100%;
                       background: linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary}, ${COLORS.primary});
                       background-size: 200% 100%;
                       border-radius: 4px;
                       animation: progress 2s ease-in-out infinite;
                   }
                   @keyframes progress {
                       0% { width: 0%; background-position: 200% 0; }
                       50% { width: 70%; background-position: 100% 0; }
                       100% { width: 100%; background-position: 0% 0; }
                   }
               `}</style>
           </>
       );
   }
   
    if (error || !egliseData) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4 dashboard-styled">
                    <Alert variant="danger" className="border-0 shadow-sm rounded-3 text-center">
                        <i className="ri-error-warning-line fs-1 mb-3 d-block text-danger"></i>
                        <h5 className="fw-semibold mb-2">Erreur</h5>
                        <p className="small mb-3">{error || 'Église non trouvée'}</p>
                        <Button variant="outline-danger" size="sm" className="rounded-pill px-4" onClick={fetchEgliseDetails}>
                            <i className="ri-refresh-line me-1"></i>Réessayer
                        </Button>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    const { eglise, stats } = egliseData;
    const roleData = generateRoleData();
    const financialData = generateFinancialData();
    const repartitionData = generateRepartitionData();

    return (
        <>
            <style>{styles}</style>
            <Header />
            <div className="main main-app p-3 p-lg-4 dashboard-styled">
                <div className="container-fluid px-0">

                    {/* En-tête moderne */}
                    <div className="header-modern mb-4 fade-in">
                        <div className="header-left">
                            <div className="header-icon-wrapper">
                                <i className="ri-building-line"></i>
                            </div>
                            <div>
                                <h5 className="gradient-text">{eglise.nom}</h5>
                                <div className="d-flex flex-wrap gap-2">
                                    <span className="badge-modern">
                                        <i className="ri-barcode-line"></i> {eglise.matricule}
                                    </span>
                                    <span className="badge-modern">
                                        <i className="ri-map-pin-line"></i> {eglise.ville || 'Ville non renseignée'}, {eglise.province || 'Province non renseignée'}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2">
                            <Button size="sm" className="btn-modern-outline" onClick={() => navigate('/eglises/sieges')}>
                                <i className="ri-arrow-left-line"></i> Retour
                            </Button>
                            <Button size="sm" className="btn-modern-primary" onClick={() => navigate(`/eglises/modifier/${id}`)}>
                                <i className="ri-edit-line"></i> Modifier
                            </Button>
                        </div>
                    </div>

                    {/* KPI Cards */}
                    <Row className="g-3 mb-4">
                        <Col xs={6} md={3}>
                            <div className="kpi-card kpi-primary">
                                <div className="kpi-icon"><i className="ri-group-line"></i></div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Membres</span>
                                    <h3 className="kpi-value">{membresData.total}</h3>
                                </div>
                            </div>
                        </Col>
                        <Col xs={6} md={3}>
                            <div className="kpi-card kpi-success">
                                <div className="kpi-icon"><i className="ri-bank-line"></i></div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Solde</span>
                                    <h3 className="kpi-value">{formatMontant(financesData.solde)}</h3>
                                </div>
                            </div>
                        </Col>
                        <Col xs={6} md={3}>
                            <div className="kpi-card kpi-warning">
                                <div className="kpi-icon"><i className="ri-calendar-event-line"></i></div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Événements</span>
                                    <h3 className="kpi-value">{evenementsData.total_evenements}</h3>
                                </div>
                            </div>
                        </Col>
                        <Col xs={6} md={3}>
                            <div className="kpi-card kpi-info">
                                <div className="kpi-icon"><i className="ri-share-line"></i></div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Temples</span>
                                    <h3 className="kpi-value">{filiales.length}</h3>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* Onglets avec fonds améliorés */}
                    <Card className="card-modern border-primary mb-4">
                        <div className="tab-modern bg-primary">
                            <Nav variant="tabs" activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
                                <Nav.Item>
                                    <Nav.Link eventKey="infos"><i className="ri-information-line me-1"></i> Informations</Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link eventKey="membres"><i className="ri-group-line me-1"></i> Membres ({membresData.total})</Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link eventKey="finances"><i className="ri-bank-line me-1"></i> Finances</Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link eventKey="filiales"><i className="ri-share-line me-1"></i> Temples ({filiales.length})</Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link eventKey="statistiques"><i className="ri-bar-chart-line me-1"></i> Statistiques</Nav.Link>
                                </Nav.Item>
                            </Nav>
                        </div>
                        <div className="tab-content-modern">

                            {/* Onglet Informations */}
                            {activeTab === 'infos' && (
                                <div className="fade-in">
                                    <Row>
                                        <Col md={8}>
                                            <div className="mb-4">
                                                <h6 className="fw-semibold mb-3" style={{ color: COLORS.primary }}>
                                                    <i className="ri-building-line me-2"></i> Détails de l'église
                                                </h6>
                                                <Row className="g-3">
                                                    <Col md={6}>
                                                        <div className="info-item">
                                                            <div className="info-label">Nom</div>
                                                            <div className="info-value">{eglise.nom}</div>
                                                        </div>
                                                    </Col>
                                                    <Col md={6}>
                                                        <div className="info-item">
                                                            <div className="info-label">Matricule</div>
                                                            <div className="info-value matricule">{eglise.matricule}</div>
                                                        </div>
                                                    </Col>
                                                    <Col md={6}>
                                                        <div className="info-item">
                                                            <div className="info-label">Date de création</div>
                                                            <div className="info-value">{formatDate(eglise.date_created)}</div>
                                                        </div>
                                                    </Col>
                                                    <Col md={6}>
                                                        <div className="info-item">
                                                            <div className="info-label">Caisse nationale</div>
                                                            <div className="info-value">
                                                                <Badge bg={eglise.caisse === 1 ? 'warning' : 'secondary'} style={{ borderRadius: '4px' }}>
                                                                    <i className={eglise.caisse === 1 ? 'ri-bank-line' : 'ri-close-line'}></i>
                                                                    {eglise.caisse === 1 ? ' Oui' : ' Non'}
                                                                </Badge>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                    <Col md={12}>
                                                        <div className="info-item">
                                                            <div className="info-label">Adresse</div>
                                                            <div className="info-value">{eglise.adresse || 'Adresse non renseignée'}</div>
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </div>

                                            <div>
                                                <h6 className="fw-semibold mb-3" style={{ color: COLORS.primary }}>
                                                    <i className="ri-contacts-line me-2"></i> Contact
                                                </h6>
                                                <Row className="g-3">
                                                    <Col md={6}>
                                                        <div className="info-item">
                                                            <div className="info-label">Téléphone</div>
                                                            <div className="info-value"><i className="ri-phone-line me-2 text-muted"></i>{eglise.tel || 'Non renseigné'}</div>
                                                        </div>
                                                    </Col>
                                                    <Col md={6}>
                                                        <div className="info-item">
                                                            <div className="info-label">Email</div>
                                                            <div className="info-value"><i className="ri-mail-line me-2 text-muted"></i>{eglise.email || 'Non renseigné'}</div>
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </div>
                                        </Col>

                                        <Col md={4}>
                                            <div className="mb-4">
                                                <h6 className="fw-semibold mb-3" style={{ color: COLORS.primary }}>
                                                    <i className="ri-map-pin-line me-2"></i> Localisation
                                                </h6>
                                                <div className="location-item">
                                                    <i className="ri-map-pin-line" style={{ color: COLORS.primary }}></i>
                                                    <div>
                                                        <div className="location-label">Province</div>
                                                        <div className="location-value">{eglise.province || 'Non renseignée'}</div>
                                                    </div>
                                                </div>
                                                <div className="location-item">
                                                    <i className="ri-building-line" style={{ color: COLORS.success }}></i>
                                                    <div>
                                                        <div className="location-label">Ville</div>
                                                        <div className="location-value">{eglise.ville || 'Non renseignée'}</div>
                                                    </div>
                                                </div>
                                                <div className="location-item">
                                                    <i className="ri-road-map-line" style={{ color: COLORS.warning }}></i>
                                                    <div>
                                                        <div className="location-label">Adresse</div>
                                                        <div className="location-value">{eglise.adresse || 'Non renseignée'}</div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div>
                                                <h6 className="fw-semibold mb-3" style={{ color: COLORS.primary }}>
                                                    <i className="ri-image-line me-2"></i> Logo
                                                </h6>
                                                {eglise.logo && eglise.logo !== 'R.jpg' && !imgError ? (
                                                    <img
                                                        src={`https://josephsbusiness.com/assets/images/${eglise.logo}`}
                                                        alt={eglise.nom}
                                                        className="logo-image"
                                                        onError={() => setImgError(true)}
                                                    />
                                                ) : (
                                                    <div className="logo-placeholder">
                                                        <i className="ri-building-line"></i>
                                                    </div>
                                                )}
                                            </div>
                                        </Col>
                                    </Row>
                                </div>
                            )}

                            {/* Onglet Membres */}
                            {activeTab === 'membres' && (
                                <div className="fade-in">
                                    <Row className="g-3 mb-4">
                                        <Col xs={6} md={3}>
                                            <div className="stats-mini-card">
                                                <div className="stats-mini-icon" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}><i className="ri-group-line"></i></div>
                                                <div>
                                                    <div className="stats-mini-label">Total</div>
                                                    <div className="stats-mini-value">{membresData.total}</div>
                                                </div>
                                            </div>
                                        </Col>
                                        <Col xs={6} md={3}>
                                            <div className="stats-mini-card">
                                                <div className="stats-mini-icon" style={{ background: `${COLORS.success}15`, color: COLORS.success }}><i className="ri-user-line"></i></div>
                                                <div>
                                                    <div className="stats-mini-label">Hommes</div>
                                                    <div className="stats-mini-value">{membresData.hommes}</div>
                                                </div>
                                            </div>
                                        </Col>
                                        <Col xs={6} md={3}>
                                            <div className="stats-mini-card">
                                                <div className="stats-mini-icon" style={{ background: '#ec489915', color: '#ec4899' }}><i className="ri-user-line"></i></div>
                                                <div>
                                                    <div className="stats-mini-label">Femmes</div>
                                                    <div className="stats-mini-value">{membresData.femmes}</div>
                                                </div>
                                            </div>
                                        </Col>
                                        <Col xs={6} md={3}>
                                            <div className="stats-mini-card">
                                                <div className="stats-mini-icon" style={{ background: '#8b5cf615', color: '#8b5cf6' }}><i className="ri-user-line"></i></div>
                                                <div>
                                                    <div className="stats-mini-label">Enfants</div>
                                                    <div className="stats-mini-value">{membresData.enfants}</div>
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>

                                    <Row className="g-4">
                                        <Col md={6}>
                                            <div className="chart-card">
                                                <div className="chart-card-header">
                                                    <span>Répartition par rôle</span>
                                                </div>
                                                <div className="p-3">
                                                    {roleData.length > 0 ? (
                                                        <ResponsiveContainer width="100%" height={250}>
                                                            <PieChart>
                                                                <Pie
                                                                    data={roleData}
                                                                    cx="50%" cy="50%"
                                                                    innerRadius={60} outerRadius={100}
                                                                    paddingAngle={5}
                                                                    dataKey="value"
                                                                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                                                >
                                                                    {roleData.map((entry, index) => (
                                                                        <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                                                                    ))}
                                                                </Pie>
                                                                <Tooltip />
                                                            </PieChart>
                                                        </ResponsiveContainer>
                                                    ) : (
                                                        <p className="text-center text-muted py-4">Aucune donnée</p>
                                                    )}
                                                </div>
                                            </div>
                                        </Col>

                                        <Col md={6}>
                                            <div className="chart-card">
                                                <div className="chart-card-header">
                                                    <span>Derniers membres inscrits</span>
                                                </div>
                                                <div className="p-0">
                                                    <Table hover className="mb-0 table-modern">
                                                        <thead>
                                                            <tr><th>Matricule</th><th>Nom & Prénom</th><th>Rôle</th></tr>
                                                        </thead>
                                                        <tbody>
                                                            {membresData.derniers_inscrits?.length > 0 ? (
                                                                membresData.derniers_inscrits.slice(0, 5).map((membre) => (
                                                                    <tr key={membre.id}>
                                                                        <td><span className="matricule-badge">{membre.matricule}</span></td>
                                                                        <td>{membre.prenom} {membre.nom}</td>
                                                                        <td><Badge bg={getRoleBadgeColor(membre.role)} style={{ borderRadius: '4px' }}>{membre.role}</Badge></td>
                                                                    </tr>
                                                                ))
                                                            ) : (
                                                                <tr><td colSpan="3" className="text-center py-3">Aucun membre</td></tr>
                                                            )}
                                                        </tbody>
                                                    </Table>
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                </div>
                            )}

                            {/* Onglet Finances */}
                            {activeTab === 'finances' && (
                                <div className="fade-in">
                                    <Row className="g-3 mb-4">
                                        <Col xs={6} md={3}>
                                            <div className="finance-stats-card">
                                                <div className="finance-stats-label">Entrées</div>
                                                <div className="finance-stats-value text-success">{formatMontant(financesData.total_entrees)}</div>
                                                <div className="finance-stats-trend">{financesData.nombre_entrees} opérations</div>
                                            </div>
                                        </Col>
                                        <Col xs={6} md={3}>
                                            <div className="finance-stats-card">
                                                <div className="finance-stats-label">Dépenses</div>
                                                <div className="finance-stats-value text-danger">{formatMontant(financesData.total_depenses)}</div>
                                                <div className="finance-stats-trend">{financesData.nombre_depenses} opérations</div>
                                            </div>
                                        </Col>
                                        <Col xs={6} md={3}>
                                            <div className="finance-stats-card">
                                                <div className="finance-stats-label">Solde</div>
                                                <div className={`finance-stats-value ${financesData.solde >= 0 ? 'text-success' : 'text-danger'}`}>
                                                    {formatMontant(financesData.solde)}
                                                </div>
                                            </div>
                                        </Col>
                                        <Col xs={6} md={3}>
                                            <div className="finance-stats-card">
                                                <div className="finance-stats-label">Biens</div>
                                                <div className="finance-stats-value text-primary">{patrimoineData.total_biens}</div>
                                                <div className="finance-stats-trend">{patrimoineData.total_quantite} articles</div>
                                            </div>
                                        </Col>
                                    </Row>

                                    <Row className="g-4">
                                        <Col md={7}>
                                            <div className="chart-card">
                                                <div className="chart-card-header">
                                                    <span>Répartition des entrées</span>
                                                </div>
                                                <div className="p-3">
                                                    {repartitionData.length > 0 ? (
                                                        <ResponsiveContainer width="100%" height={250}>
                                                            <PieChart>
                                                                <Pie
                                                                    data={repartitionData}
                                                                    cx="50%" cy="50%"
                                                                    innerRadius={60} outerRadius={100}
                                                                    paddingAngle={5}
                                                                    dataKey="value"
                                                                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                                                >
                                                                    {repartitionData.map((entry, index) => (
                                                                        <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                                                                    ))}
                                                                </Pie>
                                                                <Tooltip formatter={(value) => formatMontant(value)} />
                                                            </PieChart>
                                                        </ResponsiveContainer>
                                                    ) : (
                                                        <p className="text-center text-muted py-4">Aucune donnée</p>
                                                    )}
                                                </div>
                                            </div>
                                        </Col>

                                        <Col md={5}>
                                            <div className="chart-card">
                                                <div className="chart-card-header">
                                                    <span>Évolution mensuelle</span>
                                                </div>
                                                <div className="p-3">
                                                    {financialData.length > 0 && financialData.some(item => item.entrees > 0 || item.depenses > 0) ? (
                                                        <ResponsiveContainer width="100%" height={250}>
                                                            <BarChart data={financialData}>
                                                                <CartesianGrid strokeDasharray="3 3" stroke="#e9edf2" />
                                                                <XAxis dataKey="mois" stroke="#64748b" fontSize={11} />
                                                                <YAxis stroke="#64748b" fontSize={11} />
                                                                <Tooltip formatter={(value) => formatMontant(value)} />
                                                                <Legend />
                                                                <Bar dataKey="entrees" fill={COLORS.success} name="Entrées" />
                                                                <Bar dataKey="depenses" fill={COLORS.danger} name="Dépenses" />
                                                            </BarChart>
                                                        </ResponsiveContainer>
                                                    ) : (
                                                        <p className="text-center text-muted py-4">Aucune donnée</p>
                                                    )}
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                </div>
                            )}

                            {/* Onglet Filiales */}
                            {activeTab === 'filiales' && (
                                <div className="fade-in">
                                    <div className="chart-card">
                                        <div className="chart-card-header d-flex justify-content-between align-items-center">
                                            <span>Liste des temples</span>
                                            <Badge style={{ background: COLORS.info, borderRadius: '4px' }}>{filiales.length} temples</Badge>
                                        </div>
                                        <div className="p-0">
                                            <Table hover className="mb-0 table-modern">
                                                <thead>
                                                    <tr><th>Matricule</th><th>Nom</th><th>Ville</th><th>Province</th><th>Actions</th></tr>
                                                </thead>
                                                <tbody>
                                                    {filiales.length > 0 ? (
                                                        filiales.map((filiale) => (
                                                            <tr key={filiale.id}>
                                                                <td><span className="matricule-badge">{filiale.matricule}</span></td>
                                                                <td className="fw-semibold">{filiale.nom}</td>
                                                                <td>{filiale.ville || '-'}</td>
                                                                <td>{filiale.province || '-'}</td>
                                                                <td>
                                                                    <button className="action-btn action-btn-view" title="Voir" onClick={() => navigate(`/temples/${filiale.id}`)}>
                                                                        <i className="ri-eye-line"></i>
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr><td colSpan="5" className="text-center py-4">Aucun temple rattaché</td></tr>
                                                    )}
                                                </tbody>
                                            </Table>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Onglet Statistiques */}
                            {activeTab === 'statistiques' && (
                                <div className="fade-in">
                                    <Row className="g-4">
                                        <Col md={4}>
                                            <div className="stats-detail-card">
                                                <Card.Body className="p-3">
                                                    <h6 className="stats-detail-title">Démographie</h6>
                                                    <div className="stats-detail-item"><span>Naissances</span><span className="fw-bold">{demographieData.naissances}</span></div>
                                                    <div className="stats-detail-item"><span>Décès</span><span className="fw-bold text-danger">{demographieData.deces}</span></div>
                                                    <div className="stats-detail-item"><span>Croissance naturelle</span><span className={`fw-bold ${demographieData.croissance_naturelle >= 0 ? 'text-success' : 'text-danger'}`}>{demographieData.croissance_naturelle}</span></div>
                                                </Card.Body>
                                            </div>
                                        </Col>

                                        <Col md={4}>
                                            <div className="stats-detail-card">
                                                <Card.Body className="p-3">
                                                    <h6 className="stats-detail-title">Patrimoine</h6>
                                                    <div className="stats-detail-item"><span>Nombre de biens</span><span className="fw-bold">{patrimoineData.total_biens}</span></div>
                                                    <div className="stats-detail-item"><span>Quantité totale</span><span className="fw-bold">{patrimoineData.total_quantite}</span></div>
                                                </Card.Body>
                                            </div>
                                        </Col>

                                        <Col md={4}>
                                            <div className="stats-detail-card">
                                                <Card.Body className="p-3">
                                                    <h6 className="stats-detail-title">Activité</h6>
                                                    <div className="stats-detail-item"><span>Mariages</span><span className="fw-bold">{evenementsData.mariages}</span></div>
                                                    <div className="stats-detail-item"><span>Baptêmes</span><span className="fw-bold">{evenementsData.baptemes}</span></div>
                                                    <div className="stats-detail-item"><span>Participants</span><span className="fw-bold">{evenementsData.total_participants}</span></div>
                                                </Card.Body>
                                            </div>
                                        </Col>
                                    </Row>
                                </div>
                            )}

                        </div>
                    </Card>

                    <Footer />
                </div>
            </div>
        </>
    );
}